/**
 * MeasurementAvatar.jsx — Avatar mannequin 3D gris style "scan corporel" (STYKU-like),
 * avec des rubans de mesure BLEUS déjà intégrés dans l'image (homme/femme), pour un rendu
 * "wrap" 3D fidèle demandé par l'utilisateur (15/09/2026). Les zones ci-dessous ne dessinent
 * plus de ligne — elles définissent uniquement des zones cliquables invisibles calées sur
 * les rubans déjà visibles dans la photo, avec un halo de surbrillance au survol/sélection.
 * 6 zones essentielles (Cou retiré à la demande explicite de l'utilisateur, 15/09/2026).
 */
import React, { useState } from 'react';
import { Lightbulb } from 'lucide-react';

export const AVATAR_IMG_URLS = {
  female: 'https://customer-assets-agu9un31.emergentagent.net/job_7ec0a196-30d6-48cc-b4da-a55574a12eb3/artifacts/1sbh6pyo_rapport-du-corps-Avatar-de-corps-en-3D-min.jpeg',
  male: 'https://static.prod-images.emergentagent.com/jobs/7ec0a196-30d6-48cc-b4da-a55574a12eb3/images/892dfa523b72e2978b556cf4a4d0dc96fbb89faedca80d7f2e62146561408044.jpeg',
};

// Coordonnées (% top, bandes [left1,left2] en %) calibrées précisément par détection de
// pixels sur les 2 photos (rubans bleus déjà intégrés) — identiques à
// services/pdf_service.py::_AVATAR_ZONES.
const ZONE_DEFS = {
  female: {
    chest: { top: 31.3, bands: [[43, 58]] },
    waist: { top: 41.8, bands: [[43, 57]] },
    hips: { top: 50.7, bands: [[41, 58]] },
    biceps: { top: 33, bands: [[37, 42], [58, 63]] },
    thigh: { top: 58.3, bands: [[41, 49], [50.5, 58]] },
    calf: { top: 74.6, bands: [[42, 47], [52.5, 57.5]] },
  },
  male: {
    chest: { top: 30.7, bands: [[41, 59]] },
    waist: { top: 41.7, bands: [[42.5, 58]] },
    hips: { top: 51.1, bands: [[41, 59]] },
    biceps: { top: 31.7, bands: [[35, 40], [60, 65]] },
    thigh: { top: 59, bands: [[40.5, 50], [51, 60]] },
    calf: { top: 76.1, bands: [[41, 47], [53.5, 59.5]] },
  },
};

export const ZONE_LABELS = {
  chest: { fr: 'Poitrine', en: 'Chest' },
  waist: { fr: 'Taille', en: 'Waist' },
  hips: { fr: 'Hanches', en: 'Hips' },
  biceps: { fr: 'Biceps', en: 'Biceps' },
  thigh: { fr: 'Cuisse', en: 'Thigh' },
  calf: { fr: 'Mollet', en: 'Calf' },
};

// Astuce pro contextuelle par zone (affichée sous l'avatar au survol/sélection).
const ZONE_TIPS = {
  chest: { fr: 'Mesurez au niveau des mamelons, en respirant normalement (sans bloquer).', en: 'Measure at nipple level, breathing normally (do not hold your breath).' },
  waist: { fr: "Le matin à jeun, au niveau du nombril, sans rentrer le ventre.", en: 'In the morning, fasted, at navel level, without sucking in your stomach.' },
  hips: { fr: 'Au point le plus large des fesses, pieds joints et talons serrés.', en: 'At the widest point of the hips, feet together and heels touching.' },
  biceps: { fr: 'Bras relâché le long du corps, au point le plus large du biceps.', en: 'Arm relaxed at your side, at the widest point of the biceps.' },
  thigh: { fr: 'Juste sous le pli fessier, au point le plus large de la cuisse.', en: 'Just below the glute fold, at the widest point of the thigh.' },
  calf: { fr: 'Debout, poids réparti sur les 2 jambes, au point le plus large du mollet.', en: 'Standing, weight on both legs, at the widest point of the calf.' },
};

const ZoneOverlay = ({ zoneKey, def, active, hasVal, value, en, onSelect, onHoverChange, isHovered }) => {
  const highlighted = active || isHovered;
  // Le ruban bleu est déjà "peint" dans la photo — on pose juste un halo de surbrillance
  // (même forme de ruban arrondi) par-dessus, pour donner un retour visuel au clic/survol.
  const color = active ? '#ec4899' : isHovered ? '#38bdf8' : hasVal ? '#22c55e' : null;
  const label = en ? ZONE_LABELS[zoneKey].en : ZONE_LABELS[zoneKey].fr;
  const midLeft = (def.bands[0][0] + def.bands[0][1]) / 2;

  return (
    <>
      {def.bands.map(([x1, x2], i) => (
        <button
          key={i}
          type="button"
          onClick={() => onSelect(zoneKey)}
          onMouseEnter={() => onHoverChange(zoneKey)}
          onMouseLeave={() => onHoverChange(null)}
          className="absolute -translate-y-1/2 cursor-pointer"
          style={{ top: `${def.top}%`, left: `${x1}%`, width: `${x2 - x1}%`, height: highlighted ? 22 : 16 }}
          data-testid={`avatar-zone-${zoneKey}`}
          aria-label={label}
        >
          <span
            className="absolute left-0 right-0 top-1/2 -translate-y-1/2 rounded-full transition-all duration-200"
            style={color ? {
              height: highlighted ? 9 : 6,
              background: `linear-gradient(90deg, transparent, ${color}cc, ${color}cc, transparent)`,
              boxShadow: `0 0 ${highlighted ? 14 : 8}px ${color}99`,
            } : { height: 6, background: 'transparent' }}
          />
        </button>
      ))}
      {highlighted && (
        <div
          className="absolute -translate-x-1/2 -translate-y-full pointer-events-none z-10 whitespace-nowrap px-2.5 py-1 rounded-lg bg-black/90 border text-[11px] font-bold text-white shadow-lg"
          style={{ top: `${def.top}%`, left: `${midLeft}%`, marginTop: -10, borderColor: color }}
        >
          {label}{value != null ? ` : ${value} cm` : ''}
        </div>
      )}
    </>
  );
};

export const MeasurementAvatar = ({ measurements = {}, onSelect, selectedField, gender = 'female', en = false }) => {
  const [hovered, setHovered] = useState(null);
  const defs = ZONE_DEFS[gender] || ZONE_DEFS.female;
  const imgUrl = AVATAR_IMG_URLS[gender] || AVATAR_IMG_URLS.female;
  const activeZone = hovered || selectedField;
  const tip = activeZone ? ZONE_TIPS[activeZone] : null;

  return (
    <div className="relative w-full rounded-2xl overflow-hidden bg-zinc-900" data-testid="measurement-avatar">
      <div className="relative w-full">
        <img src={imgUrl} alt="" className="w-full h-auto block select-none" draggable={false} />
        {Object.entries(defs).map(([zoneKey, def]) => (
          <ZoneOverlay
            key={zoneKey}
            zoneKey={zoneKey}
            def={def}
            en={en}
            active={selectedField === zoneKey}
            isHovered={hovered === zoneKey}
            hasVal={measurements[zoneKey] != null}
            value={measurements[zoneKey]}
            onSelect={(k) => onSelect?.(selectedField === k ? null : k)}
            onHoverChange={setHovered}
          />
        ))}
      </div>
      <div
        key={activeZone || 'default'}
        className="flex items-start gap-1.5 text-center justify-center py-2 px-3 min-h-[34px] transition-opacity duration-200"
        data-testid="measurement-avatar-tip"
      >
        <Lightbulb className={`w-3 h-3 mt-0.5 shrink-0 ${tip ? 'text-amber-400' : 'text-zinc-600'}`} />
        <p className={`text-[10px] leading-snug ${tip ? 'text-zinc-300' : 'text-zinc-500'}`}>
          {tip ? (en ? tip.en : tip.fr) : (en ? 'Tap a highlighted line to see the exact measurement point' : 'Touchez une ligne en surbrillance pour voir précisément où mesurer')}
        </p>
      </div>
    </div>
  );
};

export default MeasurementAvatar;
